import { useState, useCallback } from 'react';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { authFetch } from '@/lib/auth-fetch';
import { usePermissions } from '@/providers/PermissionsProvider';

interface MakeAdminResult {
  success: boolean;
  message: string;
  userId?: string;
  teamId?: string;
}

interface UseMakeMeAdminReturn {
  makeAdmin: () => Promise<MakeAdminResult>;
  isLoading: boolean;
  result: MakeAdminResult | null;
  error: Error | null;
}

const getApiBaseUrl = (): string => {
  return getApiUrl() ?? getBackendUrl();
};

export function useMakeMeAdmin(): UseMakeMeAdminReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<MakeAdminResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { refreshPermissions } = usePermissions();

  const makeAdmin = useCallback(async (): Promise<MakeAdminResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await authFetch(`${apiBaseUrl}/api/teams/make-me-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: MakeAdminResult = await response.json();
      setResult(data);

      if (data.success) {
        await refreshPermissions();
      }

      return data;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      setError(errorObj);
      const failResult: MakeAdminResult = {
        success: false,
        message: errorObj.message,
      };
      setResult(failResult);
      return failResult;
    } finally {
      setIsLoading(false);
    }
  }, [refreshPermissions]);

  return {
    makeAdmin,
    isLoading,
    result,
    error,
  };
}
